import { useIdle } from '@shared/hooks';
import { dayjs } from '@shared/utils';
import { useEffect, useState } from 'react';

import { useServerTimeOffset } from '../../stores';

/**
 * Returns `true` once the server time has passed `targetTime`.
 *
 * Uses `setTimeout` internally — zero polling, exactly one re-render
 * when the gate flips from `false` to `true`.
 *
 * @param targetTime - The server time after which this returns `true`.
 */

const idleTime = 5000;

export function useTimeGate(targetTime: Date): boolean {
  const { timeOffset } = useServerTimeOffset();

  const [isPast, setIsPast] = useState(() => {
    return !dayjs().add(timeOffset, 'millisecond').isBefore(targetTime);
  });

  // Fires when the user returns to the tab (visibilitychange / focus).
  // Re-including isIdle in the effect deps ensures the timeout is
  // recalculated immediately on return, because browsers throttle/defer
  // setTimeout callbacks while the tab is in the background.
  const isIdle = useIdle(idleTime, {
    events: ['visibilitychange', 'focus'],
    initialState: false,
  });

  useEffect(() => {
    // Gate already open — nothing to schedule.
    if (isPast) return;

    const serverNow = dayjs().add(timeOffset, 'millisecond');

    if (!serverNow.isBefore(targetTime)) {
      setIsPast(true);
      return;
    }

    // setTimeout delay is a 32-bit signed int (max ≈ 24.8 days); larger values
    // overflow and fire immediately. Skip long waits — the effect re-runs on
    // focus/visibility (isIdle) and re-checks when the user comes back.
    if (dayjs(targetTime).diff(serverNow, 'day') >= 1) return;

    const msUntilTarget = dayjs(targetTime).diff(serverNow);
    const id = window.setTimeout(() => {
      setIsPast(true);
    }, msUntilTarget);

    return () => {
      window.clearTimeout(id);
    };
  }, [targetTime, timeOffset, isIdle, isPast]);

  return isPast;
}
